import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { showApiError } from "@/lib/toast";
import { codeRequest, loginWithOtp } from "@/sideEffects/api";
import { useDispatch } from "react-redux";
import { appActions } from "@/store/appSlice";
import { useTranslation } from "react-i18next";
import { LoadingSmall } from "@/components/LoadingSmall";
import { ResendOtpButton } from "./ResendOtpButton";

const formSchema = z.object({
  email: z.string(),
  password: z.string(),
  otp: z.string(),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const OtpForm: React.FC<{ email: string }> = ({ email }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email,
      password: "",
    },
  });

  const loginMutation = useMutation({
    mutationFn: (values: FormSchemaType) => {
      return loginWithOtp(email, values.password, values.otp as unknown as number);
    },
    onSuccess: (result) => {
      const token = result.data.auth_token;
      if (token) {
        dispatch(appActions.successfulLogin({ token }));
        document.location.href = "/";
      } else {
        toast.error("Login failed");
      }
    },
    onError: showApiError,
  });

  function onSubmit(values: FormSchemaType) {
    loginMutation.mutate(values);
  }

  const resendOtpMutation = useMutation({
    mutationFn: () => {
      return codeRequest(email);
    },
    onError: showApiError,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("user.email")}</FormLabel>
              <FormControl>
                <Input placeholder={t("user.email")} disabled {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("login.password")}</FormLabel>
              <FormControl>
                <Input placeholder={t("login.password")} type="password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="otp"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("login.otp")}</FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription>{t("login.otpDescription")}</FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-between">
          <Button type="submit" disabled={loginMutation.isPending}>
            {t("login.button")} {loginMutation.isPending && <LoadingSmall />}
          </Button>
          <ResendOtpButton onResendClick={() => resendOtpMutation.mutate()} />
        </div>
      </form>
    </Form>
  );
};
